import { useOrganisationContactPersonService } from 'src/services/organisation-contact-person-service';
import { useTicketContactPersonService } from 'src/services/ticket-contact-person-service';
import { ContactPersonFilterDTOMatchMode } from 'src/api/models';
import type { ContactPersonFilterDTO } from 'src/api/models';
import type {
  EntitySearchConfig,
  EntitySearchResult,
  ContactPersonEntity,
  PaginationParams,
  PaginatedResponse,
} from '../types';
import { ENTITY_ICONS, getEntityDisplayName, getEntityDisplayProps } from 'src/components/ticket-edit/entity-display';

function formatName(contact: ContactPersonEntity): string {
  const parts = [contact.first_name, contact.midlet_name, contact.last_name].filter(Boolean);
  return parts.join(' ');
}

export function createContactPersonSearchConfig(): EntitySearchConfig<ContactPersonEntity> {
  const { fetchContactPersonsByOrganisation } = useOrganisationContactPersonService();
  const { fetchTicketContactPersons } = useTicketContactPersonService();

  return {
    entityType: 'contact_person',
    icon: ENTITY_ICONS.contact_person,
    getDisplayName: (entity) => getEntityDisplayName(entity, 'contact_person'),
    getDisplayProps: (entity) => getEntityDisplayProps(entity, 'contact_person'),

    searchFields: [
      { name: 'first_name', weight: 2 },
      { name: 'last_name', weight: 2 },
      { name: 'email', weight: 1.5 },
      { name: 'phone_number', weight: 1 },
      { name: 'mobile_phone_number', weight: 1 },
    ],

    async fetchAll(pagination: PaginationParams): Promise<PaginatedResponse<ContactPersonEntity>> {
      const response = await fetchTicketContactPersons({}, pagination);
      return {
        data: (response.data || []) as ContactPersonEntity[],
        hasMore: (response.page ?? 0) < (response.total_pages ?? 0),
        totalPages: response.total_pages,
      };
    },

    async fetchByParent(
      organisationId: string,
      _pagination: PaginationParams
    ): Promise<PaginatedResponse<ContactPersonEntity>> {
      const contacts = await fetchContactPersonsByOrganisation(organisationId);
      return {
        data: contacts,
        hasMore: false,
      };
    },

    async apiSearch(
      query: string,
      pagination: PaginationParams,
      excludeIds?: string[]
    ): Promise<PaginatedResponse<ContactPersonEntity>> {
      const filters: ContactPersonFilterDTO = {
        match_mode: ContactPersonFilterDTOMatchMode.or,
        first_name: query,
        last_name: query,
      };

      const response = await fetchTicketContactPersons(filters, pagination);
      let data = (response.data || []) as ContactPersonEntity[];

      if (excludeIds && excludeIds.length > 0) {
        const excludeSet = new Set(excludeIds);
        data = data.filter((contact) => contact.id && !excludeSet.has(contact.id));
      }

      return {
        data,
        hasMore: (response.page ?? 0) < (response.total_pages ?? 0),
        totalPages: response.total_pages,
      };
    },

    formatResult(
      contact: ContactPersonEntity,
      isRelated?: boolean
    ): EntitySearchResult<ContactPersonEntity> {
      const name = formatName(contact);

      // Build subtitle - include org name for unrelated contacts
      const subtitleParts = [contact.email].filter(Boolean);
      if (!isRelated && contact.organisation?.name) {
        subtitleParts.push(`@ ${contact.organisation.name}`);
      }

      return {
        id: contact.id || '',
        title: name || 'Unknown',
        subtitle: subtitleParts.join(' - '),
        icon: 'pi-user',
        badges: contact.active === false ? [{ text: 'Inactive', variant: 'danger' as const }] : undefined,
        meta: {
          isRelated,
          organisationId: contact.organisation?.id,
          organisationName: contact.organisation?.name,
          phone: contact.phone_number,
          mobile: contact.mobile_phone_number,
        },
        originalItem: contact,
      };
    },

    getOrganisationId(contact: ContactPersonEntity): string | undefined {
      return contact.organisation?.id;
    },
  };
}
